/**
 * Document cleanup engine — CamScanner-style scan cleanup, fully client side.
 *
 * Everything runs on plain typed arrays over an ImageData (no OpenCV/wasm), so
 * the tool stays in the same "nothing leaves the browser" story as the rest of
 * the image tools.
 *
 * Pipeline (non-destructive — the source ImageData is never mutated):
 *   source -> preset -> shadow removal -> black boost -> saturation -> text deepen
 *
 * Shadow/background removal is the "divide by the illumination map" trick: the
 * page background is estimated on a small grid (max filter kills the thin dark
 * strokes, box blur smooths what is left), then every pixel is divided by that
 * map so uneven lighting flattens to paper white. Estimating on the small grid
 * keeps the result identical between the downscaled live preview and the
 * full-resolution export — all radii below are either grid-based or scaled by
 * the image's long edge for the same reason.
 */

import { warpQuadToRect } from '../shared/perspectiveHomography';

export { formatFileSize } from '../compress/compressImageEngine';

export const MAX_FILE_BYTES = 50 * 1024 * 1024;
export const ACCEPT = 'image/jpeg,image/png,image/webp,image/avif,image/heic,image/heif,.jpg,.jpeg,.png,.webp,.avif,.heic,.heif';

/** Cleanup presets, in the order they are shown in the sidebar. */
export const CLEAN_PRESETS = ['normal', 'colorClean', 'colorScan', 'bw'];

/** Live preview works on a downscaled copy so slider drags stay interactive. */
export const PREVIEW_MAX_DIM = 1280;

/** Perspective crop output is capped so huge phone photos stay printable. */
export const CROP_MAX_DIM = 2600;

/** Export cap — still above A4 at 300 dpi (2480×3508), but keeps exports quick. */
export const EXPORT_MAX_DIM = 3600;

export const DEFAULT_ADJUSTMENTS = Object.freeze({
    shadowRemoval: 0,
    blackBoost: 0,
    saturation: 0,
    textDeepen: 0,
    // The plain print-ready tone controls (the Document Print tool). Cleanup itself never sets them, so for it they stay 0 = no pass.
    brightness: 0, // -100 … 100
    contrast: 0, // -100 … 100
    grayscale: 0, // 0 | 1
});

const ALL_SLIDERS = ['shadowRemoval', 'blackBoost', 'saturation', 'textDeepen'];

/**
 * Which sliders can actually change the picture for a given preset. A pure
 * black-and-white page has no shadows and no colour left to push, so those two
 * are hidden rather than left on screen doing nothing.
 */
export function slidersForPreset(preset) {
    if (preset === 'bw') return ['blackBoost', 'textDeepen'];
    return ALL_SLIDERS;
}

const SUPPORTED_TYPES = new Set([
    'image/jpeg', 'image/png', 'image/webp', 'image/avif', 'image/heic', 'image/heif',
]);
const SUPPORTED_EXT = /\.(jpe?g|png|webp|avif|heic|heif)$/i;

/** Grid the illumination map is estimated on (long edge, in px). */
const BG_GRID = 240;

export function isSupportedDocumentFile(file) {
    if (!file) return false;
    if (file.type) return SUPPORTED_TYPES.has(file.type);
    return SUPPORTED_EXT.test(file.name || '');
}

/**
 * AVIF is decoded by the browser itself (unlike HEIC, which we convert with
 * heic2any), so on a build without an AVIF decoder the file is fine and the
 * browser is the problem — worth saying so instead of "bad file".
 */
export function isAvifFile(file) {
    if (!file) return false;
    return file.type === 'image/avif' || /\.avif$/i.test(file.name || '');
}

/* ------------------------------------------------------------------ */
/* Loading / canvas plumbing                                           */
/* ------------------------------------------------------------------ */

function loadImageFromUrl(url) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('image-decode-failed'));
        img.src = url;
    });
}

/** HEIC/HEIF phone shots are converted to JPEG first (same as the flatten tool). */
export async function normalizeDocumentFile(file) {
    const name = (file?.name || '').toLowerCase();
    const isHeic = /\.(heic|heif)$/.test(name) || file.type === 'image/heic' || file.type === 'image/heif';
    if (!isHeic) return file;

    const heic2any = (await import('heic2any')).default;
    const converted = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.92 });
    const blob = Array.isArray(converted) ? converted[0] : converted;
    return new File([blob], `${file.name.replace(/\.(heic|heif)$/i, '')}.jpg`, { type: 'image/jpeg' });
}

export async function loadDocumentImage(file) {
    const normalized = await normalizeDocumentFile(file);
    const url = URL.createObjectURL(normalized);
    try {
        return await loadImageFromUrl(url);
    } finally {
        URL.revokeObjectURL(url);
    }
}

export function imageToImageData(img) {
    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth || img.width;
    canvas.height = img.naturalHeight || img.height;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(img, 0, 0);
    return ctx.getImageData(0, 0, canvas.width, canvas.height);
}

export function imageDataToCanvas(imageData) {
    const canvas = document.createElement('canvas');
    canvas.width = imageData.width;
    canvas.height = imageData.height;
    canvas.getContext('2d').putImageData(imageData, 0, 0);
    return canvas;
}

export function canvasToImageData(canvas) {
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    return ctx.getImageData(0, 0, canvas.width, canvas.height);
}

export function imageDataToDataUrl(imageData, mime = 'image/jpeg', quality = 0.92) {
    return imageDataToCanvas(imageData).toDataURL(mime, quality);
}

export function imageDataToBlob(imageData, mime = 'image/jpeg', quality = 0.92) {
    const canvas = imageDataToCanvas(imageData);
    return new Promise((resolve, reject) => {
        canvas.toBlob(
            (blob) => (blob ? resolve(blob) : reject(new Error('encode-failed'))),
            mime,
            mime === 'image/png' ? undefined : quality,
        );
    });
}

/** Downscale through the browser's own resampler (fast + smooth). */
export function downscaleImageData(imageData, maxDim) {
    const { width, height } = imageData;
    const scale = Math.min(1, maxDim / Math.max(width, height));
    if (scale >= 1) return imageData;

    const target = document.createElement('canvas');
    target.width = Math.max(1, Math.round(width * scale));
    target.height = Math.max(1, Math.round(height * scale));
    const ctx = target.getContext('2d', { willReadFrequently: true });
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(imageDataToCanvas(imageData), 0, 0, target.width, target.height);
    return canvasToImageData(target);
}

/**
 * Perspective-warp a quad out of an image, capped at CROP_MAX_DIM.
 * The quad is [TL, TR, BR, BL] in the image's own pixel coordinates.
 */
export function warpDocumentQuad(imageData, quad) {
    const dist = (a, b) => Math.hypot(b[0] - a[0], b[1] - a[1]);
    const width = (dist(quad[0], quad[1]) + dist(quad[3], quad[2])) / 2;
    const height = (dist(quad[0], quad[3]) + dist(quad[1], quad[2])) / 2;
    const scale = Math.min(1, CROP_MAX_DIM / Math.max(width, height, 1));
    const outW = Math.max(1, Math.round(width * scale));
    const outH = Math.max(1, Math.round(height * scale));
    return canvasToImageData(warpQuadToRect(imageDataToCanvas(imageData), quad, outW, outH));
}

/**
 * Turn a quad with the image it belongs to, so a crop survives a rotation
 * instead of pointing at the wrong corner of the page.
 */
export function rotateQuad(quad, turns, width, height) {
    const steps = ((Math.round(turns) % 4) + 4) % 4;
    let points = quad.map((p) => [...p]);
    let w = width;
    let h = height;
    for (let i = 0; i < steps; i++) {
        const turned = points.map(([x, y]) => [h - y, x]);
        // Moving the points is only half of it — a clockwise turn also RENAMES the
        // corners (the old bottom-left is the new top-left). Without this the quad
        // is still [TL, TR, BR, BL] by index but not by meaning, and the warp comes
        // out rotated 90° from the crop the user drew.
        points = [turned[3], turned[0], turned[1], turned[2]];
        const swap = w;
        w = h;
        h = swap;
    }
    return points;
}

/** Rotate by a multiple of 90° (clockwise). */
export function rotateImageData(imageData, degrees) {
    const turns = ((Math.round(degrees / 90) % 4) + 4) % 4;
    if (turns === 0) return imageData;

    const { width: w, height: h } = imageData;
    const swap = turns % 2 === 1;
    const out = document.createElement('canvas');
    out.width = swap ? h : w;
    out.height = swap ? w : h;

    const ctx = out.getContext('2d', { willReadFrequently: true });
    ctx.translate(out.width / 2, out.height / 2);
    ctx.rotate((turns * 90 * Math.PI) / 180);
    ctx.drawImage(imageDataToCanvas(imageData), -w / 2, -h / 2);
    return canvasToImageData(out);
}

/* ------------------------------------------------------------------ */
/* Channel helpers                                                     */
/* ------------------------------------------------------------------ */

function splitChannels(data, size) {
    const r = new Float32Array(size);
    const g = new Float32Array(size);
    const b = new Float32Array(size);
    for (let i = 0, p = 0; i < size; i++, p += 4) {
        r[i] = data[p];
        g[i] = data[p + 1];
        b[i] = data[p + 2];
    }
    return [r, g, b];
}

function mergeChannels(r, g, b, source, size) {
    const out = new Uint8ClampedArray(size * 4);
    for (let i = 0, p = 0; i < size; i++, p += 4) {
        out[p] = r[i];
        out[p + 1] = g[i];
        out[p + 2] = b[i];
        out[p + 3] = source[p + 3];
    }
    return out;
}

function toGray(r, g, b, size) {
    const gray = new Float32Array(size);
    for (let i = 0; i < size; i++) {
        gray[i] = 0.299 * r[i] + 0.587 * g[i] + 0.114 * b[i];
    }
    return gray;
}

/* ------------------------------------------------------------------ */
/* Small-grid filters (used for the illumination map)                  */
/* ------------------------------------------------------------------ */

function gridSize(w, h) {
    const scale = Math.min(1, BG_GRID / Math.max(w, h));
    return {
        sw: Math.max(2, Math.round(w * scale)),
        sh: Math.max(2, Math.round(h * scale)),
    };
}

/** Box-average downsample of a single channel. */
function downsampleChannel(src, w, h, sw, sh) {
    const out = new Float32Array(sw * sh);
    const xRatio = w / sw;
    const yRatio = h / sh;

    for (let y = 0; y < sh; y++) {
        const y0 = Math.floor(y * yRatio);
        const y1 = Math.min(h, Math.max(y0 + 1, Math.floor((y + 1) * yRatio)));
        for (let x = 0; x < sw; x++) {
            const x0 = Math.floor(x * xRatio);
            const x1 = Math.min(w, Math.max(x0 + 1, Math.floor((x + 1) * xRatio)));
            let sum = 0;
            let count = 0;
            for (let yy = y0; yy < y1; yy++) {
                const row = yy * w;
                for (let xx = x0; xx < x1; xx++) {
                    sum += src[row + xx];
                    count++;
                }
            }
            out[y * sw + x] = count ? sum / count : 0;
        }
    }
    return out;
}

/** Separable max filter (dilate) — swallows dark text so only paper is left. */
function maxFilter(src, w, h, radius) {
    const tmp = new Float32Array(w * h);
    const out = new Float32Array(w * h);

    for (let y = 0; y < h; y++) {
        const row = y * w;
        for (let x = 0; x < w; x++) {
            let best = -Infinity;
            const from = Math.max(0, x - radius);
            const to = Math.min(w - 1, x + radius);
            for (let xx = from; xx <= to; xx++) {
                const v = src[row + xx];
                if (v > best) best = v;
            }
            tmp[row + x] = best;
        }
    }

    for (let x = 0; x < w; x++) {
        for (let y = 0; y < h; y++) {
            let best = -Infinity;
            const from = Math.max(0, y - radius);
            const to = Math.min(h - 1, y + radius);
            for (let yy = from; yy <= to; yy++) {
                const v = tmp[yy * w + x];
                if (v > best) best = v;
            }
            out[y * w + x] = best;
        }
    }
    return out;
}

/** Separable box blur with a running sum. */
function boxBlur(src, w, h, radius) {
    if (radius < 1) return src;
    const tmp = new Float32Array(w * h);
    const out = new Float32Array(w * h);
    const span = radius * 2 + 1;

    for (let y = 0; y < h; y++) {
        const row = y * w;
        let sum = 0;
        for (let x = -radius; x <= radius; x++) {
            sum += src[row + Math.min(w - 1, Math.max(0, x))];
        }
        for (let x = 0; x < w; x++) {
            tmp[row + x] = sum / span;
            sum -= src[row + Math.min(w - 1, Math.max(0, x - radius))];
            sum += src[row + Math.min(w - 1, Math.max(0, x + radius + 1))];
        }
    }

    for (let x = 0; x < w; x++) {
        let sum = 0;
        for (let y = -radius; y <= radius; y++) {
            sum += tmp[Math.min(h - 1, Math.max(0, y)) * w + x];
        }
        for (let y = 0; y < h; y++) {
            out[y * w + x] = sum / span;
            sum -= tmp[Math.min(h - 1, Math.max(0, y - radius)) * w + x];
            sum += tmp[Math.min(h - 1, Math.max(0, y + radius + 1)) * w + x];
        }
    }
    return out;
}

/** Illumination map of one channel, on the small grid. */
function estimateBackground(channel, w, h, sw, sh) {
    const small = downsampleChannel(channel, w, h, sw, sh);
    const dilated = maxFilter(small, sw, sh, 2);
    return boxBlur(dilated, sw, sh, 3);
}

/**
 * Divide a channel by its (bilinearly upsampled) illumination map, in place.
 * `strength` in [0,1] blends between the original and the fully flattened value.
 */
function divideByBackground(channel, w, h, bg, sw, sh, strength) {
    if (strength <= 0) return;
    const xRatio = sw / w;
    const yRatio = sh / h;

    for (let y = 0; y < h; y++) {
        const fy = Math.min(sh - 1, Math.max(0, (y + 0.5) * yRatio - 0.5));
        const y0 = Math.floor(fy);
        const y1 = Math.min(sh - 1, y0 + 1);
        const wy = fy - y0;
        const rowA = y0 * sw;
        const rowB = y1 * sw;
        const outRow = y * w;

        for (let x = 0; x < w; x++) {
            const fx = Math.min(sw - 1, Math.max(0, (x + 0.5) * xRatio - 0.5));
            const x0 = Math.floor(fx);
            const x1 = Math.min(sw - 1, x0 + 1);
            const wx = fx - x0;

            const top = bg[rowA + x0] * (1 - wx) + bg[rowA + x1] * wx;
            const bottom = bg[rowB + x0] * (1 - wx) + bg[rowB + x1] * wx;
            const level = top * (1 - wy) + bottom * wy;

            const value = channel[outRow + x];
            const flattened = level > 1 ? (value * 255) / level : 255;
            const next = value + (Math.min(255, flattened) - value) * strength;
            channel[outRow + x] = next < 0 ? 0 : next > 255 ? 255 : next;
        }
    }
}

/** Flatten lighting using one shared (luminance) map — keeps colours intact. */
function removeShadowsLuma(r, g, b, w, h, strength) {
    if (strength <= 0) return;
    const size = w * h;
    const { sw, sh } = gridSize(w, h);
    const bg = estimateBackground(toGray(r, g, b, size), w, h, sw, sh);
    divideByBackground(r, w, h, bg, sw, sh, strength);
    divideByBackground(g, w, h, bg, sw, sh, strength);
    divideByBackground(b, w, h, bg, sw, sh, strength);
}

/** Flatten each channel on its own map — also neutralises paper colour casts. */
function removeShadowsPerChannel(r, g, b, w, h, strength) {
    if (strength <= 0) return;
    const { sw, sh } = gridSize(w, h);
    for (const channel of [r, g, b]) {
        const bg = estimateBackground(channel, w, h, sw, sh);
        divideByBackground(channel, w, h, bg, sw, sh, strength);
    }
}

/* ------------------------------------------------------------------ */
/* Tone / detail operators                                             */
/* ------------------------------------------------------------------ */

/**
 * Levels WHITE point: everything at or above `255 - amount*70` snaps to paper
 * white. This is what "erase shadows and stains" means once a preset has already
 * flattened the lighting — the grey blotches a divide leaves behind are near-white,
 * and re-running the divide on an already-white page does nothing at all.
 */
function applyWhitePoint(channels, size, amount) {
    if (amount <= 0) return;
    const scale = 255 / (255 - amount * 70);
    for (const channel of channels) {
        for (let i = 0; i < size; i++) {
            const v = channel[i] * scale;
            channel[i] = v > 255 ? 255 : v;
        }
    }
}

/**
 * Levels BLACK point: everything at or below `amount*90` goes to solid black and
 * the rest is re-stretched. "Increase blacks" — the old gain+offset mostly
 * *brightened*, which is why the slider looked dead on an already-white page.
 */
function applyBlackPoint(channels, size, amount) {
    if (amount <= 0) return;
    const black = amount * 90;
    const scale = 255 / (255 - black);
    for (const channel of channels) {
        for (let i = 0; i < size; i++) {
            const v = (channel[i] - black) * scale;
            channel[i] = v < 0 ? 0 : v > 255 ? 255 : v;
        }
    }
}

/** Linear levels: gain around black, offset. Applied to every channel. */
function applyLevels(channels, size, gain, offset) {
    if (gain === 1 && offset === 0) return;
    for (const channel of channels) {
        for (let i = 0; i < size; i++) {
            const v = channel[i] * gain + offset;
            channel[i] = v < 0 ? 0 : v > 255 ? 255 : v;
        }
    }
}

/** Saturation around the pixel's own luminance. */
function applySaturation(r, g, b, size, scale) {
    if (scale === 1) return;
    for (let i = 0; i < size; i++) {
        const lum = 0.299 * r[i] + 0.587 * g[i] + 0.114 * b[i];
        const nr = lum + (r[i] - lum) * scale;
        const ng = lum + (g[i] - lum) * scale;
        const nb = lum + (b[i] - lum) * scale;
        r[i] = nr < 0 ? 0 : nr > 255 ? 255 : nr;
        g[i] = ng < 0 ? 0 : ng > 255 ? 255 : ng;
        b[i] = nb < 0 ? 0 : nb > 255 ? 255 : nb;
    }
}

/**
 * Brightness + contrast as ONE 256-entry lookup. Brightness is a gamma (it lifts or sinks the mid-tones but keeps pure black and
 * pure white, so a white page does not clip); contrast is a straight stretch around mid-grey. Both are -1 … 1.
 */
function buildToneLut(brightness, contrast) {
    const gamma = 2 ** (-brightness * 1.2); // +1 → 0.43 (brighter), -1 → 2.3 (darker)
    const c = contrast * 127;
    const factor = (259 * (c + 255)) / (255 * (259 - c));
    const lut = new Float32Array(256);
    for (let i = 0; i < 256; i++) {
        const v = (255 * (i / 255) ** gamma - 128) * factor + 128;
        lut[i] = v < 0 ? 0 : v > 255 ? 255 : v;
    }
    return lut;
}

function applyToneLut(channels, size, lut) {
    for (const channel of channels) {
        for (let i = 0; i < size; i++) {
            const v = channel[i];
            channel[i] = lut[v < 0 ? 0 : v > 255 ? 255 : v | 0];
        }
    }
}

/** Every channel becomes the pixel's own luminance. */
function applyGrayscale(r, g, b, size) {
    for (let i = 0; i < size; i++) {
        const lum = 0.299 * r[i] + 0.587 * g[i] + 0.114 * b[i];
        r[i] = lum;
        g[i] = lum;
        b[i] = lum;
    }
}

/** Separable min filter (erode) — thickens dark strokes. */
function minFilter(src, w, h, radius) {
    const tmp = new Float32Array(w * h);
    const out = new Float32Array(w * h);

    for (let y = 0; y < h; y++) {
        const row = y * w;
        for (let x = 0; x < w; x++) {
            let best = Infinity;
            const from = Math.max(0, x - radius);
            const to = Math.min(w - 1, x + radius);
            for (let xx = from; xx <= to; xx++) {
                const v = src[row + xx];
                if (v < best) best = v;
            }
            tmp[row + x] = best;
        }
    }

    for (let x = 0; x < w; x++) {
        for (let y = 0; y < h; y++) {
            let best = Infinity;
            const from = Math.max(0, y - radius);
            const to = Math.min(h - 1, y + radius);
            for (let yy = from; yy <= to; yy++) {
                const v = tmp[yy * w + x];
                if (v < best) best = v;
            }
            out[y * w + x] = best;
        }
    }
    return out;
}

/** Blend an eroded copy back in — the "make the handwriting darker" slider. */
function applyTextDeepen(channels, w, h, amount) {
    if (amount <= 0) return;
    const radius = Math.max(1, Math.round(Math.max(w, h) / 900));
    const size = w * h;
    for (const channel of channels) {
        const eroded = minFilter(channel, w, h, radius);
        for (let i = 0; i < size; i++) {
            channel[i] = channel[i] + (eroded[i] - channel[i]) * amount;
        }
    }
}

/** Unsharp mask at a resolution-independent radius. */
function applyUnsharp(channels, w, h, amount) {
    if (amount <= 0) return;
    const radius = Math.max(1, Math.round(Math.max(w, h) / 700));
    const size = w * h;
    for (const channel of channels) {
        const blurred = boxBlur(channel, w, h, radius);
        for (let i = 0; i < size; i++) {
            const v = channel[i] + amount * (channel[i] - blurred[i]);
            channel[i] = v < 0 ? 0 : v > 255 ? 255 : v;
        }
    }
}

/**
 * CLAHE on a single plane — tile histograms, clipped and redistributed, then
 * bilinearly interpolated between the four surrounding tile LUTs.
 */
function clahePlane(plane, w, h, clipLimit, tiles) {
    const tilesX = tiles;
    const tilesY = tiles;
    const tileW = Math.ceil(w / tilesX);
    const tileH = Math.ceil(h / tilesY);
    const luts = new Array(tilesX * tilesY);

    for (let ty = 0; ty < tilesY; ty++) {
        for (let tx = 0; tx < tilesX; tx++) {
            const x0 = tx * tileW;
            const y0 = ty * tileH;
            const x1 = Math.min(w, x0 + tileW);
            const y1 = Math.min(h, y0 + tileH);

            const hist = new Int32Array(256);
            let count = 0;
            for (let y = y0; y < y1; y++) {
                const row = y * w;
                for (let x = x0; x < x1; x++) {
                    const v = plane[row + x];
                    hist[v < 0 ? 0 : v > 255 ? 255 : v | 0]++;
                    count++;
                }
            }

            const limit = Math.max(1, Math.round((clipLimit * count) / 256));
            let excess = 0;
            for (let i = 0; i < 256; i++) {
                if (hist[i] > limit) {
                    excess += hist[i] - limit;
                    hist[i] = limit;
                }
            }
            const share = Math.floor(excess / 256);
            let leftover = excess - share * 256;
            for (let i = 0; i < 256; i++) {
                hist[i] += share;
                if (leftover > 0) {
                    hist[i] += 1;
                    leftover--;
                }
            }

            const lut = new Uint8Array(256);
            let cumulative = 0;
            const total = count || 1;
            for (let i = 0; i < 256; i++) {
                cumulative += hist[i];
                lut[i] = Math.round((cumulative * 255) / total);
            }
            luts[ty * tilesX + tx] = lut;
        }
    }

    const out = new Float32Array(w * h);
    for (let y = 0; y < h; y++) {
        const gy = Math.min(tilesY - 1, Math.max(0, (y - tileH / 2) / tileH));
        const ty0 = Math.floor(gy);
        const ty1 = Math.min(tilesY - 1, ty0 + 1);
        const fy = gy - ty0;
        const row = y * w;

        for (let x = 0; x < w; x++) {
            const gx = Math.min(tilesX - 1, Math.max(0, (x - tileW / 2) / tileW));
            const tx0 = Math.floor(gx);
            const tx1 = Math.min(tilesX - 1, tx0 + 1);
            const fx = gx - tx0;

            const raw = plane[row + x];
            const v = raw < 0 ? 0 : raw > 255 ? 255 : raw | 0;

            const a = luts[ty0 * tilesX + tx0][v];
            const b = luts[ty0 * tilesX + tx1][v];
            const c = luts[ty1 * tilesX + tx0][v];
            const d = luts[ty1 * tilesX + tx1][v];

            out[row + x] = (a * (1 - fx) + b * fx) * (1 - fy) + (c * (1 - fx) + d * fx) * fy;
        }
    }
    return out;
}

/** Local-mean adaptive threshold via an integral image. */
function adaptiveThreshold(gray, w, h, radius, offset) {
    const iw = w + 1;
    const integral = new Float64Array(iw * (h + 1));
    for (let y = 0; y < h; y++) {
        let rowSum = 0;
        for (let x = 0; x < w; x++) {
            rowSum += gray[y * w + x];
            integral[(y + 1) * iw + (x + 1)] = integral[y * iw + (x + 1)] + rowSum;
        }
    }

    const out = new Float32Array(w * h);
    for (let y = 0; y < h; y++) {
        const y0 = Math.max(0, y - radius);
        const y1 = Math.min(h - 1, y + radius);
        for (let x = 0; x < w; x++) {
            const x0 = Math.max(0, x - radius);
            const x1 = Math.min(w - 1, x + radius);
            const area = (x1 - x0 + 1) * (y1 - y0 + 1);
            const sum =
                integral[(y1 + 1) * iw + (x1 + 1)] -
                integral[y0 * iw + (x1 + 1)] -
                integral[(y1 + 1) * iw + x0] +
                integral[y0 * iw + x0];
            const mean = sum / area;
            out[y * w + x] = gray[y * w + x] > mean - offset ? 255 : 0;
        }
    }
    return out;
}

/* ------------------------------------------------------------------ */
/* Presets                                                             */
/* ------------------------------------------------------------------ */

function applyPreset(preset, r, g, b, w, h, adjustments) {
    const size = w * h;

    if (preset === 'normal') {
        // Plain scan: flatten the lighting, then a gentle levels push.
        removeShadowsLuma(r, g, b, w, h, 1);
        applyLevels([r, g, b], size, 1.18, -14);
        return null;
    }

    if (preset === 'colorClean') {
        // Per-channel flattening whitens the paper and kills colour casts,
        // then a light unsharp brings the strokes back.
        removeShadowsPerChannel(r, g, b, w, h, 1);
        applyLevels([r, g, b], size, 1.12, -10);
        applyUnsharp([r, g, b], w, h, 0.6);
        return null;
    }

    if (preset === 'colorScan') {
        // Keeps the photo look — only a partial de-shadow — but equalises local
        // contrast (CLAHE on value) and lifts the colours back up afterwards.
        removeShadowsLuma(r, g, b, w, h, 0.6);
        const value = new Float32Array(size);
        for (let i = 0; i < size; i++) {
            value[i] = Math.max(r[i], g[i], b[i]);
        }
        const equalised = clahePlane(value, w, h, 3, 8);
        for (let i = 0; i < size; i++) {
            const from = value[i];
            if (from < 1) continue;
            const gain = equalised[i] / from;
            const nr = r[i] * gain;
            const ng = g[i] * gain;
            const nb = b[i] * gain;
            r[i] = nr > 255 ? 255 : nr;
            g[i] = ng > 255 ? 255 : ng;
            b[i] = nb > 255 ? 255 : nb;
        }
        applyLevels([r, g, b], size, 1.14, -12);
        applySaturation(r, g, b, size, 1.2);
        return null;
    }

    if (preset === 'bw') {
        // Pure black/white: flatten first so the threshold is not fooled by
        // shadow, then a local-mean threshold at a size-independent radius.
        //
        // "Increase blacks" has to feed the THRESHOLD here — a levels curve on a
        // 0-or-255 image can't do anything. A smaller offset means a pixel has to
        // be closer to its local mean to count as paper, so more faint pencil is
        // kept as ink.
        removeShadowsLuma(r, g, b, w, h, 1);
        const gray = toGray(r, g, b, size);
        const radius = Math.max(4, Math.round(Math.max(w, h) / 70));
        const offset = 14 - (adjustments?.blackBoost || 0) / 100 * 12;
        const binary = adaptiveThreshold(gray, w, h, radius, offset);
        r.set(binary);
        g.set(binary);
        b.set(binary);
        return 'binary';
    }

    return null;
}

/**
 * Run the whole cleanup pipeline over an ImageData.
 *
 * @param {ImageData} source untouched — a fresh ImageData is returned
 * @param {{ preset?: string, adjustments?: typeof DEFAULT_ADJUSTMENTS }} options
 * @returns {ImageData}
 */
export function processDocument(source, options = {}) {
    const { preset = 'none', adjustments = DEFAULT_ADJUSTMENTS } = options;
    const w = source.width;
    const h = source.height;
    const size = w * h;

    const [r, g, b] = splitChannels(source.data, size);
    const mode = CLEAN_PRESETS.includes(preset)
        ? applyPreset(preset, r, g, b, w, h, adjustments)
        : null;
    const isBinary = mode === 'binary';

    const shadowRemoval = Math.max(0, Math.min(100, adjustments.shadowRemoval || 0)) / 100;
    if (shadowRemoval > 0 && !isBinary) {
        // Two jobs, because the useful one depends on what came before: an extra
        // de-shadow pass (all that matters on a raw photo with no preset) and a
        // white-point lift (all that matters after a preset has already flattened
        // the page — that pass is a no-op on paper that is already 255).
        removeShadowsLuma(r, g, b, w, h, shadowRemoval);
        applyWhitePoint([r, g, b], size, shadowRemoval);
    }

    const blackBoost = Math.max(0, Math.min(100, adjustments.blackBoost || 0)) / 100;
    if (blackBoost > 0 && !isBinary) {
        // The bw preset consumed this already (it moves the threshold instead).
        applyBlackPoint([r, g, b], size, blackBoost);
    }

    const saturation = Math.max(0, Math.min(100, adjustments.saturation || 0)) / 100;
    if (saturation > 0 && !isBinary) {
        applySaturation(r, g, b, size, 1 + saturation);
    }

    const textDeepen = Math.max(0, Math.min(100, adjustments.textDeepen || 0)) / 100;
    if (textDeepen > 0) {
        applyTextDeepen([r, g, b], w, h, textDeepen);
    }

    // Plain tone controls last, so they act on whatever the cleanup produced.
    const brightness = Math.max(-100, Math.min(100, adjustments.brightness || 0)) / 100;
    const contrast = Math.max(-100, Math.min(100, adjustments.contrast || 0)) / 100;
    if (brightness !== 0 || contrast !== 0) {
        applyToneLut([r, g, b], size, buildToneLut(brightness, contrast));
    }
    if (adjustments.grayscale) applyGrayscale(r, g, b, size);

    return new ImageData(mergeChannels(r, g, b, source.data, size), w, h);
}

export function outputFileName(file, extension = 'jpg') {
    const base = (file?.name || 'document').replace(/\.[^.]+$/, '');
    return `${base}-cleaned.${extension}`;
}
